import "@/App.css";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth, roleHome } from "@/context/AuthContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import AppShell from "@/components/AppShell";
import PortalShell from "@/components/PortalShell";
import LoginPage from "@/pages/Login";
import DashboardPage from "@/pages/Dashboard";
import CustomersPage from "@/pages/Customers";
import NewCustomerPage from "@/pages/NewCustomer";
import CustomerDetailPage from "@/pages/CustomerDetail";
import PrintLabelPage from "@/pages/PrintLabel";
import NfcProgrammingPage from "@/pages/NfcProgramming";
import StandsPage from "@/pages/Stands";
import StandDetailPage from "@/pages/StandDetail";
import StandLabelPage from "@/pages/StandLabel";
import SettingsPage from "@/pages/Settings";
import PortalUsersPage from "@/pages/admin/PortalUsers";
import PortalDashboardPage from "@/pages/portal/PortalDashboard";
import MyBusinessPage from "@/pages/portal/MyBusiness";
import PortalBusinessDetailPage from "@/pages/portal/PortalBusinessDetail";
import StatisticsPage from "@/pages/portal/Statistics";
import AccountPage from "@/pages/portal/Account";
import RedirectPage from "@/pages/RedirectPage";
import NotFoundPage from "@/pages/NotFound";

function AdminOnly({ children }) {
  return (
    <ProtectedRoute role="admin">
      <AppShell>{children}</AppShell>
    </ProtectedRoute>
  );
}

function CustomerOnly({ children }) {
  return (
    <ProtectedRoute role="customer">
      <PortalShell>{children}</PortalShell>
    </ProtectedRoute>
  );
}

function RootRedirect() {
  const { user } = useAuth();
  if (user === null) return null;
  if (user === false) return <Navigate to="/login" replace />;
  return <Navigate to={roleHome(user.role)} replace />;
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <Routes>
            {/* Public */}
            <Route path="/login" element={<LoginPage />} />
            <Route path="/r/:slug" element={<RedirectPage />} />
            <Route path="/t/:code" element={<RedirectPage />} />

            {/* Role-based root */}
            <Route path="/" element={<RootRedirect />} />

            {/* Admin */}
            <Route path="/dashboard" element={<AdminOnly><DashboardPage /></AdminOnly>} />
            <Route path="/kunden" element={<AdminOnly><CustomersPage /></AdminOnly>} />
            <Route path="/kunden/neu" element={<AdminOnly><NewCustomerPage /></AdminOnly>} />
            <Route path="/kunden/:id" element={<AdminOnly><CustomerDetailPage /></AdminOnly>} />
            <Route path="/kunden/:id/label" element={<AdminOnly><PrintLabelPage /></AdminOnly>} />
            <Route path="/kunden/:id/nfc" element={<AdminOnly><NfcProgrammingPage /></AdminOnly>} />
            <Route path="/aufsteller" element={<AdminOnly><StandsPage /></AdminOnly>} />
            <Route path="/aufsteller/:id" element={<AdminOnly><StandDetailPage /></AdminOnly>} />
            <Route path="/aufsteller/:id/label" element={<AdminOnly><StandLabelPage /></AdminOnly>} />
            <Route path="/kunden-zugaenge" element={<AdminOnly><PortalUsersPage /></AdminOnly>} />
            <Route path="/einstellungen" element={<AdminOnly><SettingsPage /></AdminOnly>} />

            {/* Customer portal */}
            <Route
              path="/customer/dashboard"
              element={<CustomerOnly><PortalDashboardPage /></CustomerOnly>}
            />
            <Route
              path="/customer/business"
              element={<CustomerOnly><MyBusinessPage /></CustomerOnly>}
            />
            <Route
              path="/customer/business/:id"
              element={<CustomerOnly><PortalBusinessDetailPage /></CustomerOnly>}
            />
            <Route
              path="/customer/statistik"
              element={<CustomerOnly><StatisticsPage /></CustomerOnly>}
            />
            <Route
              path="/customer/konto"
              element={<CustomerOnly><AccountPage /></CustomerOnly>}
            />

            <Route path="*" element={<NotFoundPage />} />
          </Routes>
          <Toaster
            position="top-right"
            theme="dark"
            richColors
            toastOptions={{
              classNames: {
                toast: "bg-card border border-white/[0.06]",
              },
            }}
          />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
